import React, { useState } from 'react';
import './IconPalette.css';

/**
 * IconPalette Component
 * 
 * Renders a visual palette of icons for standardized data selection.
 * Supports single or multiple selection modes.
 * Each icon has coded metadata attached for clean, queryable data.
 * 
 * Props:
 * - config: Icon palette configuration from data type
 * - value: Currently selected icon(s)
 * - onChange: Callback when selection changes
 * - disabled: Disable selection
 */
const IconPalette = ({ config, value, onChange, disabled = false }) => {
  const [selectedIds, setSelectedIds] = useState(() => {
    if (!value) return config.selectionMode === 'multiple' ? [] : null;
    if (config.selectionMode === 'multiple') {
      return Array.isArray(value) ? value.map(v => v.id) : [];
    }
    return value.id || null;
  });

  const handleIconClick = (icon) => {
    if (disabled) return;

    if (config.selectionMode === 'multiple') {
      // Multiple selection mode
      const newSelectedIds = selectedIds.includes(icon.id)
        ? selectedIds.filter(id => id !== icon.id) // Deselect
        : [...selectedIds, icon.id]; // Add to selection
      
      setSelectedIds(newSelectedIds);
      
      // Return array of full icon objects
      const selectedIcons = config.icons.filter(i => newSelectedIds.includes(i.id));
      onChange(selectedIcons);
    } else {
      // Single selection mode
      const newSelectedId = selectedIds === icon.id ? null : icon.id; // Toggle
      setSelectedIds(newSelectedId);
      
      // Return single icon object or null
      const selectedIcon = newSelectedId ? config.icons.find(i => i.id === newSelectedId) : null;
      onChange(selectedIcon);
    }
  };

  const isSelected = (iconId) => {
    if (config.selectionMode === 'multiple') {
      return selectedIds.includes(iconId);
    }
    return selectedIds === iconId;
  };

  if (!config || !config.icons || config.icons.length === 0) {
    return null;
  }

  return (
    <div className="icon-palette-container">
      {config.label && (
        <label className="icon-palette-label">
          {config.label}
          {config.required && <span className="required-indicator">*</span>}
        </label>
      )}
      
      <div className="icon-palette-grid">
        {config.icons.map((icon) => (
          <button
            key={icon.id}
            type="button"
            className={`icon-palette-item ${isSelected(icon.id) ? 'selected' : ''} ${disabled ? 'disabled' : ''}`}
            onClick={() => handleIconClick(icon)}
            disabled={disabled}
            title={icon.tooltip || icon.label}
            style={{
              '--icon-color': icon.color
            }}
          >
            <span className="icon-symbol" role="img" aria-label={icon.label}>
              {icon.icon}
            </span>
            <span className="icon-label">{icon.label}</span>
            {isSelected(icon.id) && (
              <span className="selection-indicator">✓</span>
            )}
          </button>
        ))}
      </div>

      {config.selectionMode === 'multiple' && selectedIds.length > 0 && (
        <div className="selection-summary">
          Selected: {selectedIds.length} {selectedIds.length === 1 ? 'item' : 'items'}
        </div>
      )}
    </div>
  );
};

export default IconPalette;
